import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {DatepickerHandle} from 'components/common/Datepicker';
import {PhoneInputHandle} from 'components/common/PhoneInput';
import {useVirtualKeyboard as useVirtualKeyInner} from 'hooks/useVirtualKeyboard';
import {useVirtualKeyboardSearchSelects} from 'hooks/useVirtualKeyboard/useVirtualKeyboardSearchSelects';
import {FormTypes} from 'utils/guestForm';
import {VirtualKeyboardContext} from './VirtualKeyboardContext';
import type {
  DatepickerRefsMap,
  PhoneInputRefsMap,
  InputFieldHandler,
} from './VirtualKeyboardContext';
import {GUEST_FORM_NAMES} from '@chekin/core';

const countrySelectIds = [
  GUEST_FORM_NAMES.nationality,
  GUEST_FORM_NAMES.birth_place_country,
  GUEST_FORM_NAMES.citizenship,
  GUEST_FORM_NAMES.document_expedition_country,
  GUEST_FORM_NAMES.residence_country,
  GUEST_FORM_NAMES.arrived_from_country,
  GUEST_FORM_NAMES.next_destination_country,
];

type VirtualKeyboardProviderProps = {
  children: ReactNode;
};

export const VirtualKeyboardProvider = ({children}: VirtualKeyboardProviderProps) => {
  const {getValues, setValue} = useFormContext<FormTypes>();
  const clearInputRef = useRef<(() => void) | null>(null);
  const [customFields, setCustomFields] = useState<Record<string, InputFieldHandler>>({});

  const registerCustomField = useCallback(
    (fieldId: string, handlers: InputFieldHandler) => {
      setCustomFields(prev => ({...prev, [fieldId]: handlers}));
      return () => {
        setCustomFields(prev => {
          const {[fieldId]: _, ...rest} = prev;
          return rest;
        });
      };
    },
    [],
  );

  const birthDateRef = useRef<DatepickerHandle>(null);
  const documentIssueDateRef = useRef<DatepickerHandle>(null);
  const documentExpirationDateRef = useRef<DatepickerHandle>(null);

  const datepickerRefs = useMemo<DatepickerRefsMap>(
    () => ({
      [GUEST_FORM_NAMES.birth_date]: birthDateRef,
      [GUEST_FORM_NAMES.document_issue_date]: documentIssueDateRef,
      [GUEST_FORM_NAMES.document_expiration_date]: documentExpirationDateRef,
    }),
    [],
  );

  const phoneRef = useRef<PhoneInputHandle>(null);
  const secondPhoneRef = useRef<PhoneInputHandle>(null);

  const phoneInputRefs = useMemo<PhoneInputRefsMap>(
    () => ({
      [GUEST_FORM_NAMES.phone]: phoneRef,
      [GUEST_FORM_NAMES.second_phone]: secondPhoneRef,
    }),
    [],
  );

  const countrySelects = useVirtualKeyboardSearchSelects({
    fieldIds: countrySelectIds,
    onBlur: () => clearInputRef.current?.(),
  });

  const virtualKeyboardConfig = useMemo(
    () => ({
      fields: {
        [GUEST_FORM_NAMES.name]: {
          getValue: () => getValues(GUEST_FORM_NAMES.name) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.name, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.surname]: {
          getValue: () => getValues(GUEST_FORM_NAMES.surname) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.surname, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.second_surname]: {
          getValue: () => getValues(GUEST_FORM_NAMES.second_surname) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.second_surname, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.birth_place_address]: {
          getValue: () => getValues(GUEST_FORM_NAMES.birth_place_address) || '',
          setValue: (value: string) =>
            setValue(GUEST_FORM_NAMES.birth_place_address, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.document_number]: {
          getValue: () => getValues(GUEST_FORM_NAMES.document_number) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.document_number, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.email]: {
          getValue: () => getValues(GUEST_FORM_NAMES.email) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.email, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.full_tourist_tax]: {
          getValue: () => getValues(GUEST_FORM_NAMES.full_tourist_tax) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.full_tourist_tax, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.residence_address]: {
          getValue: () => getValues(GUEST_FORM_NAMES.residence_address) || '',
          setValue: (value: string) =>
            setValue(GUEST_FORM_NAMES.residence_address, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.residence_postal_code]: {
          getValue: () => getValues(GUEST_FORM_NAMES.residence_postal_code) || '',
          setValue: (value: string) =>
            setValue(GUEST_FORM_NAMES.residence_postal_code, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.next_destination_address]: {
          getValue: () => getValues(GUEST_FORM_NAMES.next_destination_address) || '',
          setValue: (value: string) =>
            setValue(GUEST_FORM_NAMES.next_destination_address, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.fiscal_code]: {
          getValue: () => getValues(GUEST_FORM_NAMES.fiscal_code) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.fiscal_code, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.document_support_number]: {
          getValue: () => getValues(GUEST_FORM_NAMES.document_support_number) || '',
          setValue: (value: string) =>
            setValue(GUEST_FORM_NAMES.document_support_number, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.visa_number]: {
          getValue: () => getValues(GUEST_FORM_NAMES.visa_number) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.visa_number, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.recipient_code]: {
          getValue: () => getValues(GUEST_FORM_NAMES.recipient_code) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.recipient_code, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.vat_number]: {
          getValue: () => getValues(GUEST_FORM_NAMES.vat_number) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.vat_number, value),
          onBlur: () => clearInputRef.current?.(),
        },
        [GUEST_FORM_NAMES.chip_card]: {
          getValue: () => getValues(GUEST_FORM_NAMES.chip_card) || '',
          setValue: (value: string) => setValue(GUEST_FORM_NAMES.chip_card, value),
          onBlur: () => clearInputRef.current?.(),
        },

        [`${GUEST_FORM_NAMES.birth_date}_day`]: {
          getValue: () => birthDateRef.current?.getDayValue() || '',
          setValue: (value: string) => birthDateRef.current?.setDayValue(value),
          onBlur: () => clearInputRef.current?.(),
        },
        [`${GUEST_FORM_NAMES.birth_date}_year`]: {
          getValue: () => birthDateRef.current?.getYearValue() || '',
          setValue: (value: string) => birthDateRef.current?.setYearValue(value),
          onBlur: () => clearInputRef.current?.(),
        },
        [`${GUEST_FORM_NAMES.document_issue_date}_day`]: {
          getValue: () => documentIssueDateRef.current?.getDayValue() || '',
          setValue: (value: string) => documentIssueDateRef.current?.setDayValue(value),
          onBlur: () => clearInputRef.current?.(),
        },
        [`${GUEST_FORM_NAMES.document_issue_date}_year`]: {
          getValue: () => documentIssueDateRef.current?.getYearValue() || '',
          setValue: (value: string) => documentIssueDateRef.current?.setYearValue(value),
          onBlur: () => clearInputRef.current?.(),
        },
        [`${GUEST_FORM_NAMES.document_expiration_date}_day`]: {
          getValue: () => documentExpirationDateRef.current?.getDayValue() || '',
          setValue: (value: string) =>
            documentExpirationDateRef.current?.setDayValue(value),
          onBlur: () => clearInputRef.current?.(),
        },
        [`${GUEST_FORM_NAMES.document_expiration_date}_year`]: {
          getValue: () => documentExpirationDateRef.current?.getYearValue() || '',
          setValue: (value: string) =>
            documentExpirationDateRef.current?.setYearValue(value),
          onBlur: () => clearInputRef.current?.(),
        },
        [`${GUEST_FORM_NAMES.phone}_code`]: {
          getValue: () => phoneRef.current?.getCodeSearchValue() || '',
          setValue: (value: string) => phoneRef.current?.setCodeSearchValue(value),
          onBlur: () => clearInputRef.current?.(),
        },
        [`${GUEST_FORM_NAMES.phone}_number`]: {
          getValue: () => phoneRef.current?.getNumberValue() || '',
          setValue: (value: string) => phoneRef.current?.setNumberValue(value),
          onBlur: () => clearInputRef.current?.(),
        },
        [`${GUEST_FORM_NAMES.second_phone}_code`]: {
          getValue: () => secondPhoneRef.current?.getCodeSearchValue() || '',
          setValue: (value: string) => secondPhoneRef.current?.setCodeSearchValue(value),
          onBlur: () => clearInputRef.current?.(),
        },
        [`${GUEST_FORM_NAMES.second_phone}_number`]: {
          getValue: () => secondPhoneRef.current?.getNumberValue() || '',
          setValue: (value: string) => secondPhoneRef.current?.setNumberValue(value),
          onBlur: () => clearInputRef.current?.(),
        },
        ...countrySelects.fields,
        ...customFields,
      },
    }),
    [getValues, setValue, countrySelects.fields, customFields],
  );

  const {handleFocus, handleBlur, isVisible, activeField, clearInput, keyboard} =
    useVirtualKeyInner(virtualKeyboardConfig);

  useEffect(() => {
    clearInputRef.current = clearInput;
  }, [clearInput]);

  const contextValue = useMemo(
    () => ({
      handleFocus,
      handleBlur,
      isKeyboardVisible: isVisible,
      activeFieldId: activeField,
      datepickerRefs,
      phoneInputRefs,
      countrySelectInputsRegister: countrySelects.register,
      registerCustomField,
    }),
    [
      handleFocus,
      handleBlur,
      isVisible,
      activeField,
      datepickerRefs,
      phoneInputRefs,
      countrySelects.register,
      registerCustomField,
    ],
  );

  return (
    <VirtualKeyboardContext.Provider value={contextValue}>
      {children}
      {keyboard}
    </VirtualKeyboardContext.Provider>
  );
};
